import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DaySummary } from "../lib/api";
import { fillMissingDays } from "../lib/dates";

type Props = {
  days: DaySummary[];
  goal: { calories: number; protein_g: number };
  tz: string;
};

export function WeeklyChart({ days, goal, tz }: Props) {
  const filled = fillMissingDays(days, 7, tz);
  const data = filled.map((d) => ({
    label: shortLabel(d.day, tz),
    calories: d.calories,
    protein_g: d.protein_g,
  }));

  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
          <XAxis dataKey="label" stroke="var(--muted)" />
          <YAxis
            yAxisId="left"
            stroke="var(--muted)"
            domain={[0, (dataMax: number) => Math.max(dataMax, goal.calories) * 1.1]}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="var(--muted)"
            domain={[0, (dataMax: number) => Math.max(dataMax, goal.protein_g) * 1.1]}
          />
          <Tooltip
            contentStyle={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
          <Legend />
          <ReferenceLine
            yAxisId="left"
            y={goal.calories}
            stroke="var(--accent-cal)"
            strokeDasharray="4 4"
            label={{ value: "kcal goal", fill: "var(--muted)", fontSize: 10, position: "insideTopRight" }}
          />
          <ReferenceLine
            yAxisId="right"
            y={goal.protein_g}
            stroke="var(--accent-pro)"
            strokeDasharray="4 4"
            label={{ value: "protein goal", fill: "var(--muted)", fontSize: 10, position: "insideBottomLeft" }}
          />
          <Bar
            yAxisId="left"
            dataKey="calories"
            name="Calories"
            fill="var(--accent-cal)"
            radius={[6, 6, 0, 0]}
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.calories > 0 ? "var(--accent-cal)" : "var(--grid)"} />
            ))}
          </Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="protein_g"
            name="Protein (g)"
            stroke="var(--accent-pro)"
            strokeWidth={2.5}
            dot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function shortLabel(day: string, tz: string): string {
  const d = new Date(`${day}T00:00:00`);
  return new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: tz }).format(d);
}
